import {
    useInfiniteQuery,
    useMutation,
    useQuery,
    useQueryClient,
} from '@tanstack/react-query'
import { toast } from 'sonner'

import type {
    CreateInNoteRequest,
    CreateOutNoteRequest,
    CreateWithdrawalNoteRequest,
    ListLotsParams,
    ListNotesParams,
    ListWithdrawalNotesParams,
    UpdateInNoteRequest,
    UpdateOutNoteRequest,
} from '@/lib/api/sorting-lot-api'
import { sortingLotApi } from '@/lib/api/sorting-lot-api'
import { purchaseLotKeys } from '@/hooks/use-purchase-lot-query'
import { m } from '@/lib/paraglide/messages'

// ============================================================================
// Query keys
// ============================================================================

export const sortingLotKeys = {
    all: ['sorting-lots'] as const,
    lots: () => [...sortingLotKeys.all, 'lots'] as const,
    lotList: (params: ListLotsParams) =>
        [...sortingLotKeys.lots(), 'list', params] as const,
    lotDetail: (id: string) =>
        [...sortingLotKeys.lots(), 'detail', id] as const,

    inNotes: () => [...sortingLotKeys.all, 'in-notes'] as const,
    inNoteList: (params: ListNotesParams) =>
        [...sortingLotKeys.inNotes(), 'list', params] as const,
    inNoteDetail: (id: string) =>
        [...sortingLotKeys.inNotes(), 'detail', id] as const,

    outNotes: () => [...sortingLotKeys.all, 'out-notes'] as const,
    outNoteList: (params: ListNotesParams) =>
        [...sortingLotKeys.outNotes(), 'list', params] as const,
    outNoteDetail: (id: string) =>
        [...sortingLotKeys.outNotes(), 'detail', id] as const,
    withdrawalNotes: () => [...sortingLotKeys.all, 'withdrawal-notes'] as const,
    withdrawalNoteList: (params: ListWithdrawalNotesParams) =>
        [...sortingLotKeys.withdrawalNotes(), 'list', params] as const,
    withdrawalNoteDetail: (id: string) =>
        [...sortingLotKeys.withdrawalNotes(), 'detail', id] as const,

    outNotesForScenting: (search: string) =>
        [...sortingLotKeys.outNotes(), 'for-scenting', search] as const,
}

// ============================================================================
// Lot queries
// ============================================================================

export function useSortingLots(
    params: ListLotsParams = {},
    options?: { enabled?: boolean },
) {
    return useQuery({
        enabled: options?.enabled ?? true,
        queryKey: sortingLotKeys.lotList(params),
        queryFn: () => sortingLotApi.listLots(params),
    })
}

/**
 * Infinite variant cho mobile "Xem thêm" pattern. Accumulate pages 1..N.
 * `enabled` false-safe: khi caller pass false (vd viewport không phải mobile),
 * hook không fetch.
 */
export function useSortingLotsInfinite(
    params: Omit<ListLotsParams, 'page'> = {},
    options?: { enabled?: boolean },
) {
    return useInfiniteQuery({
        enabled: options?.enabled ?? true,
        queryKey: [...sortingLotKeys.lots(), 'infinite', params] as const,
        queryFn: ({ pageParam }) =>
            sortingLotApi.listLots({ ...params, page: pageParam }),
        initialPageParam: 1,
        getNextPageParam: (lastPage) => {
            const meta = lastPage.meta
            if (!meta) return undefined
            return meta.page < meta.total_pages ? meta.page + 1 : undefined
        },
    })
}

export function useSortingLot(id: string | undefined) {
    return useQuery({
        enabled: !!id,
        queryKey: sortingLotKeys.lotDetail(id ?? ''),
        queryFn: () => sortingLotApi.getLot(id as string),
    })
}

export function useSortingLotHistory(id: string | undefined) {
    return useInfiniteQuery({
        enabled: !!id,
        queryKey: [...sortingLotKeys.lots(), 'history', id ?? ''] as const,
        queryFn: ({ pageParam }) =>
            sortingLotApi.getLotHistory(id as string, {
                page: pageParam,
                per_page: 20,
            }),
        initialPageParam: 1,
        getNextPageParam: (lastPage) => {
            const meta = lastPage.meta
            if (!meta) return undefined
            return meta.page < meta.total_pages ? meta.page + 1 : undefined
        },
    })
}

// ============================================================================
// Stock-in note queries
// ============================================================================

export function useSortingInNotes(
    params: ListNotesParams = {},
    options?: { enabled?: boolean },
) {
    return useQuery({
        enabled: options?.enabled ?? true,
        queryKey: sortingLotKeys.inNoteList(params),
        queryFn: () => sortingLotApi.listInNotes(params),
    })
}

/**
 * Infinite variant cho mobile "Xem thêm" pattern. Accumulate pages 1..N.
 * `enabled` false-safe: khi caller pass false (vd viewport không phải mobile),
 * hook không fetch.
 */
export function useSortingInNotesInfinite(
    params: Omit<ListNotesParams, 'page'> = {},
    options?: { enabled?: boolean },
) {
    return useInfiniteQuery({
        enabled: options?.enabled ?? true,
        queryKey: [...sortingLotKeys.inNotes(), 'infinite', params] as const,
        queryFn: ({ pageParam }) =>
            sortingLotApi.listInNotes({ ...params, page: pageParam }),
        initialPageParam: 1,
        getNextPageParam: (lastPage) => {
            const meta = lastPage.meta
            if (!meta) return undefined
            return meta.page < meta.total_pages ? meta.page + 1 : undefined
        },
    })
}

export function useSortingInNote(id: string | undefined) {
    return useQuery({
        enabled: !!id,
        queryKey: sortingLotKeys.inNoteDetail(id ?? ''),
        queryFn: () => sortingLotApi.getInNote(id as string),
    })
}

export function useCreateSortingInNote() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (req: CreateInNoteRequest) =>
            sortingLotApi.createInNote(req),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: sortingLotKeys.inNotes() })
            qc.invalidateQueries({ queryKey: sortingLotKeys.lots() })
            qc.invalidateQueries({ queryKey: purchaseLotKeys.withdrawalNotes() })
            toast.success(m.sl_toast_create_pnk())
        },
    })
}

export function useUpdateSortingInNote() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: ({ id, req }: { id: string; req: UpdateInNoteRequest }) =>
            sortingLotApi.updateInNote(id, req),
        onSuccess: (_data, { id }) => {
            qc.invalidateQueries({ queryKey: sortingLotKeys.inNoteDetail(id) })
            qc.invalidateQueries({ queryKey: sortingLotKeys.inNotes() })
            qc.invalidateQueries({ queryKey: sortingLotKeys.lots() })
            qc.invalidateQueries({ queryKey: purchaseLotKeys.withdrawalNotes() })
            toast.success(m.sl_toast_update_pnk())
        },
    })
}

export function useApproveSortingInNote() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (id: string) => sortingLotApi.approveInNote(id),
        onSuccess: (_data, id) => {
            qc.invalidateQueries({ queryKey: sortingLotKeys.inNoteDetail(id) })
            qc.invalidateQueries({ queryKey: sortingLotKeys.inNotes() })
            qc.invalidateQueries({ queryKey: sortingLotKeys.lots() })
            toast.success(m.sl_toast_approve_pnk())
        },
    })
}

export function useRejectSortingInNote() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: ({ id, reason }: { id: string; reason: string }) =>
            sortingLotApi.rejectInNote(id, reason),
        onSuccess: (_data, { id }) => {
            qc.invalidateQueries({ queryKey: sortingLotKeys.inNoteDetail(id) })
            qc.invalidateQueries({ queryKey: sortingLotKeys.inNotes() })
            qc.invalidateQueries({ queryKey: purchaseLotKeys.withdrawalNotes() })
            toast.success(m.sl_toast_reject_pnk())
        },
    })
}

export function useCancelSortingInNote() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (id: string) => sortingLotApi.cancelInNote(id),
        onSuccess: (_data, id) => {
            qc.invalidateQueries({ queryKey: sortingLotKeys.inNoteDetail(id) })
            qc.invalidateQueries({ queryKey: sortingLotKeys.inNotes() })
            qc.invalidateQueries({ queryKey: purchaseLotKeys.withdrawalNotes() })
            toast.success(m.sl_toast_cancel_pnk())
        },
    })
}

// ============================================================================
// Stock-out note queries
// ============================================================================

/**
 * PXK lô phân loại đã duyệt, lý do "để lấy hương" — nguồn hợp lệ cho PNK lô lấy
 * hương. Dùng cho picker ở form tạo PNK.
 */
export function useSortingOutNotesForScenting(
    search = '',
    options?: { enabled?: boolean },
) {
    return useQuery({
        enabled: options?.enabled ?? true,
        queryKey: sortingLotKeys.outNotesForScenting(search),
        queryFn: () =>
            sortingLotApi.listOutNotesForScenting(search || undefined),
    })
}

export function useSortingOutNotes(
    params: ListNotesParams = {},
    options?: { enabled?: boolean },
) {
    return useQuery({
        enabled: options?.enabled ?? true,
        queryKey: sortingLotKeys.outNoteList(params),
        queryFn: () => sortingLotApi.listOutNotes(params),
    })
}

/**
 * Infinite variant cho mobile "Xem thêm" pattern. Accumulate pages 1..N.
 * `enabled` false-safe: khi caller pass false (vd viewport không phải mobile),
 * hook không fetch.
 */
export function useSortingOutNotesInfinite(
    params: Omit<ListNotesParams, 'page'> = {},
    options?: { enabled?: boolean },
) {
    return useInfiniteQuery({
        enabled: options?.enabled ?? true,
        queryKey: [...sortingLotKeys.outNotes(), 'infinite', params] as const,
        queryFn: ({ pageParam }) =>
            sortingLotApi.listOutNotes({ ...params, page: pageParam }),
        initialPageParam: 1,
        getNextPageParam: (lastPage) => {
            const meta = lastPage.meta
            if (!meta) return undefined
            return meta.page < meta.total_pages ? meta.page + 1 : undefined
        },
    })
}

export function useSortingOutNote(id: string | undefined) {
    return useQuery({
        enabled: !!id,
        queryKey: sortingLotKeys.outNoteDetail(id ?? ''),
        queryFn: () => sortingLotApi.getOutNote(id as string),
    })
}

export function useCreateSortingOutNote() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (req: CreateOutNoteRequest) =>
            sortingLotApi.createOutNote(req),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: sortingLotKeys.outNotes() })
            toast.success(m.sl_toast_create_pxk())
        },
    })
}

export function useUpdateSortingOutNote() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: ({ id, req }: { id: string; req: UpdateOutNoteRequest }) =>
            sortingLotApi.updateOutNote(id, req),
        onSuccess: (_data, { id }) => {
            qc.invalidateQueries({ queryKey: sortingLotKeys.outNoteDetail(id) })
            qc.invalidateQueries({ queryKey: sortingLotKeys.outNotes() })
            qc.invalidateQueries({ queryKey: sortingLotKeys.lots() })
            toast.success(m.sl_toast_update_pxk())
        },
    })
}

export function useApproveSortingOutNote() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (id: string) => sortingLotApi.approveOutNote(id),
        onSuccess: (_data, id) => {
            qc.invalidateQueries({ queryKey: sortingLotKeys.outNoteDetail(id) })
            qc.invalidateQueries({ queryKey: sortingLotKeys.outNotes() })
            qc.invalidateQueries({ queryKey: sortingLotKeys.lots() })
            toast.success(m.sl_toast_approve_pxk())
        },
    })
}

export function useRejectSortingOutNote() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: ({ id, reason }: { id: string; reason: string }) =>
            sortingLotApi.rejectOutNote(id, reason),
        onSuccess: (_data, { id }) => {
            qc.invalidateQueries({ queryKey: sortingLotKeys.outNoteDetail(id) })
            qc.invalidateQueries({ queryKey: sortingLotKeys.outNotes() })
            toast.success(m.sl_toast_reject_pxk())
        },
    })
}

export function useCancelSortingOutNote() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (id: string) => sortingLotApi.cancelOutNote(id),
        onSuccess: (_data, id) => {
            qc.invalidateQueries({ queryKey: sortingLotKeys.outNoteDetail(id) })
            qc.invalidateQueries({ queryKey: sortingLotKeys.outNotes() })
            toast.success(m.sl_toast_cancel_pxk())
        },
    })
}

// ============================================================================
// Phiếu lấy hàng (withdrawal note) queries
// ============================================================================

export function useSortingWithdrawalNotes(
    params: ListWithdrawalNotesParams = {},
    options?: { enabled?: boolean },
) {
    return useQuery({
        enabled: options?.enabled ?? true,
        queryKey: sortingLotKeys.withdrawalNoteList(params),
        queryFn: () => sortingLotApi.listWithdrawalNotes(params),
    })
}

/** Infinite variant cho mobile "Xem thêm" pattern. */
export function useSortingWithdrawalNotesInfinite(
    params: Omit<ListWithdrawalNotesParams, 'page'> = {},
    options?: { enabled?: boolean },
) {
    return useInfiniteQuery({
        enabled: options?.enabled ?? true,
        queryKey: [
            ...sortingLotKeys.withdrawalNotes(),
            'infinite',
            params,
        ] as const,
        queryFn: ({ pageParam }) =>
            sortingLotApi.listWithdrawalNotes({ ...params, page: pageParam }),
        initialPageParam: 1,
        getNextPageParam: (lastPage) => {
            const meta = lastPage.meta
            if (!meta) return undefined
            return meta.page < meta.total_pages ? meta.page + 1 : undefined
        },
    })
}

export function useSortingWithdrawalNote(id: string | undefined) {
    return useQuery({
        enabled: !!id,
        queryKey: sortingLotKeys.withdrawalNoteDetail(id ?? ''),
        queryFn: () => sortingLotApi.getWithdrawalNote(id as string),
    })
}

export function useCreateSortingWithdrawalNote() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (req: CreateWithdrawalNoteRequest) =>
            sortingLotApi.createWithdrawalNote(req),
        onSuccess: () => {
            qc.invalidateQueries({
                queryKey: sortingLotKeys.withdrawalNotes(),
            })
            toast.success(m.sl_toast_create_plh())
        },
    })
}

export function useCancelSortingWithdrawalNote() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (id: string) => sortingLotApi.cancelWithdrawalNote(id),
        onSuccess: (_data, id) => {
            qc.invalidateQueries({
                queryKey: sortingLotKeys.withdrawalNoteDetail(id),
            })
            qc.invalidateQueries({
                queryKey: sortingLotKeys.withdrawalNotes(),
            })
            toast.success(m.sl_toast_cancel_plh())
        },
    })
}
